import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { LENS_HINTS, useGraphLens, type Lens } from '@/store/graphLens'
import { useUI } from '@/store/ui'
import { GraphAPI } from '@/api/graph'
import { DomainsAPI } from '@/api/domains'
import {
  ACTIVITY_DOT,
  AE,
  domainActivity,
  domainColorMap,
  domainStats,
  EGO,
} from '@/lib/graphColors'
import { HelpHint } from '@/components/HelpHint'

// The View tab: how the constellation SPEAKS — lens picker with plain-language hints, and the
// legend. The legend is regenerated from the LIVE domains (declared regions ∪ node domains), not a
// fixed taxonomy: each row is one region with its hue, entity count, and activity dot. It reads from
// the same query cache as the canvas (['domains'], ['graph-network','all']) and builds the colour map
// with the same inputs, so a domain's colour here can never drift from its wedge in the sky.

const LENSES: { key: Lens; label: string }[] = [
  { key: 'structure', label: 'structure' },
  { key: 'heat', label: 'heat' },
  { key: 'freshness', label: 'freshness' },
  { key: 'confidence', label: 'confidence' },
]

export function GraphViewTab() {
  const { lens, setLens } = useGraphLens()
  const { diwaUrl } = useUI()
  const { data: domainsData } = useQuery({ queryKey: ['domains'], queryFn: () => new DomainsAPI(diwaUrl).list() })
  const { data: net } = useQuery({
    queryKey: ['graph-network', 'all'],
    queryFn: () => new GraphAPI(diwaUrl).network('all'),
  })

  const legend = useMemo(() => {
    const nodes = net?.nodes ?? []
    // Stats over REAL nodes only: the ego is code-defined and already has its own "You" row
    // above — its built-in home (crm) must not read as a populated, "live" region when it
    // holds nothing but you. Ego-only built-ins vanish; one holding real entities shows as
    // "You / ego". The colour map still feeds on ALL node domains so hues match the canvas.
    const egoDomain = nodes.find((n) => n.self)?.domain ?? null
    const stats = domainStats(nodes.filter((n) => !n.self))
    const declared = domainsData?.domains ?? []
    const colors = domainColorMap([...declared.map((d) => d.slug), ...nodes.map((n) => n.domain)])
    const seen = new Set(declared.map((d) => d.slug))
    const rows = [
      ...declared.map((d) => ({ slug: d.slug, name: d.name, status: d.status })),
      ...[...stats.keys()]
        .filter((slug) => !seen.has(slug))
        .sort()
        .map((slug) => ({ slug, name: slug === egoDomain ? 'You / ego' : slug, status: 'active' })),
    ]
    return rows.map((d) => {
      const stat = stats.get(d.slug)
      const count = stat?.count ?? 0
      return {
        ...d,
        color: colors.get(d.slug) ?? AE.ash,
        count,
        activity: domainActivity({
          count,
          archived: d.status === 'archived',
          lastTouched: stat?.lastTouched ?? null,
        }),
      }
    })
  }, [net, domainsData])

  return (
    <div>
      <div className="dw-rail-section-title">
        Lens{' '}
        <HelpHint gloss="A lens changes what a star's size and brightness MEAN on the canvas — one channel at a time, so the sky stays legible." />
      </div>
      <div className="dw-chip-row" style={{ marginBottom: 6 }}>
        {LENSES.map((l) => (
          <button
            key={l.key}
            type="button"
            aria-pressed={lens === l.key}
            title={LENS_HINTS[l.key]}
            className={`dw-chip dw-chip--toggle${lens === l.key ? ' dw-chip--on' : ''}`}
            onClick={() => setLens(l.key)}
          >
            {l.label}
          </button>
        ))}
      </div>
      <div className="dw-notice" style={{ marginBottom: 14 }}>{LENS_HINTS[lens]}</div>

      <div className="dw-rail-section-title">
        Legend{' '}
        <HelpHint gloss="Your world's live regions — each wears one colour, identical here and in the sky. The green dot means a spring touched that region within the last week." />
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        {/* You — the only star with a coloured core; every region is a haloed hue below. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'calc(11px * var(--ae-text-scale, 1))', color: 'var(--ae-color-text-secondary)' }}>
          <span style={{ width: 9, height: 9, borderRadius: 999, background: EGO.core, display: 'inline-block', flex: '0 0 auto' }} />
          You
        </div>
        {legend.map((d) => (
          <div key={d.slug} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'calc(11px * var(--ae-text-scale, 1))', color: 'var(--ae-color-text-secondary)' }}>
            <span style={{ width: 9, height: 9, borderRadius: 999, background: d.color, display: 'inline-block', flex: '0 0 auto' }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
            <span style={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}>{d.count}</span>
            <span
              aria-hidden
              title={d.activity === 'active' ? 'a spring ran recently' : 'quiet'}
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                background: ACTIVITY_DOT[d.activity],
                boxShadow: d.activity === 'active' ? `0 0 4px ${ACTIVITY_DOT.active}` : undefined,
                flex: '0 0 auto',
              }}
            />
          </div>
        ))}
        {legend.length === 0 && (
          <div className="dw-rail-empty" style={{ fontSize: 'calc(11px * var(--ae-text-scale, 1))' }}>No domains yet.</div>
        )}
      </div>

      <div className="dw-rail-section-title" style={{ marginTop: 14 }}>Edges</div>
      <div style={{ fontSize: 'calc(11px * var(--ae-text-scale, 1))', color: 'var(--ae-color-text-muted)', lineHeight: 1.7 }}>
        <HelpHint gloss="A solid line is a relation you created or approved — settled fact.">solid = confirmed</HelpHint>
        {' · '}
        <HelpHint gloss="A dashed line is Diwa's inference — a relation it believes exists, awaiting your confirmation.">dashed = inferred</HelpHint>
        {' · '}
        <HelpHint gloss="A dotted line is pending — proposed but not yet real.">dotted = pending</HelpHint>
      </div>
    </div>
  )
}
